import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { trpc } from "../lib/trpc";
import { useProgressStream } from "../hooks/useSSE";
import { Button, Card, Badge, Spinner } from "../components/ui";
import { fmtDateTime } from "../lib/format";
import { ColumnFilter } from "../components/ColumnFilter";

// Human breakdown of a change set's ops by what they change, e.g. "525 create · 1717 sales price …".
const REASON_LABELS: Record<string, string> = {
  create: "create",
  reactivate: "reactivate",
  discontinue: "discontinue",
  orphaned: "discontinue (gone from PBS)",
  salesPrice: "sales price",
  purchasePrice: "purchase price",
  description: "description",
  visibility: "visibility",
  price: "price",
};
/**
 * The data flow a change set belongs to, shown so the source → target is clear at a glance.
 * PBS is the source of truth for ExactOnline; **ExactOnline** is the source of truth for Lightspeed (the webshop
 * also carries other vendors' items, so ExactOnline is brought 100% in line first, then Lightspeed follows).
 */
export function FlowLabel({ target }: { target: string }) {
  const [from, to] = target === "lightspeed" ? ["ExactOnline", "Lightspeed"] : ["PBS", "ExactOnline"];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm">
      <span className="font-medium text-slate-500">{from}</span>
      <span className="text-slate-400">→</span>
      <span className="font-semibold text-slate-900">{to}</span>
    </span>
  );
}

function OpsBreakdown({ byReason }: { byReason: Record<string, number> }) {
  const items = Object.entries(byReason).filter(([, n]) => n > 0).map(([k, n]) => ({ k, n, label: REASON_LABELS[k] ?? k }));
  if (!items.length) return <span className="text-xs text-slate-400">no changes</span>;
  return (
    <div className="mt-1 flex flex-col gap-0.5">
      {items.map((it) => (
        <div key={it.k} className="flex items-baseline gap-2 text-xs">
          <span className="w-12 text-right font-mono font-semibold tabular-nums text-slate-700">{it.n.toLocaleString()}</span>
          <span className="text-slate-500">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/** The reconcile's phases, in the order the engine emits them — turns a bare step name into a sentence. */
const RECON_ORDER: string[] = ["load", "diff", "verify", "store"];
const RECON_PHASES: Record<string, string> = {
  load: "Reading the snapshots",
  diff: "Comparing and planning",
  verify: "Checking against the live shop",
  store: "Writing the change set",
  skipped: "Skipped",
  done: "Done",
};

const FLOW_LABELS: Record<string, string> = { exact: "PBS → ExactOnline", lightspeed: "ExactOnline → Lightspeed", mixed: "Mixed" };
type FlowValue = "exact" | "lightspeed" | "mixed";
type StatusValue = "pending_approval" | "approved" | "applying" | "applied" | "partially_failed" | "failed" | "cancelled";
type ApprovalValue = "required" | "auto";

export default function Reconcile() {
  const utils = trpc.useUtils();
  // Column filters — sent to the server so `limit` counts MATCHING sets (filtering here would only ever
  // search the newest 30). Empty array = no filter on that column.
  const [fFlow, setFFlow] = useState<FlowValue[]>([]);
  const [fStatus, setFStatus] = useState<StatusValue[]>([]);
  const [fApproval, setFApproval] = useState<ApprovalValue[]>([]);
  const filtersActive = fFlow.length + fStatus.length + fApproval.length > 0;
  const filterOptions = trpc.reconcile.changeSetFilterOptions.useQuery(undefined, { staleTime: 30_000 });
  const sets = trpc.reconcile.changeSets.useQuery(
    {
      limit: 30,
      targets: fFlow.length ? fFlow : undefined,
      statuses: fStatus.length ? fStatus : undefined,
      approval: fApproval.length ? fApproval : undefined,
    },
    { refetchInterval: 5000 },
  );
  // Live per-set apply progress for the sets that could be mid-apply (cheap: only applying/partial sets).
  const activeIds = (sets.data ?? []).filter((c) => c.status === "applying" || c.status === "partially_failed").map((c) => c.id);
  const progress = trpc.reconcile.applyProgress.useQuery({ ids: activeIds }, { enabled: activeIds.length > 0, refetchInterval: 3000 });
  const { byJob } = useProgressStream();
  const running = trpc.reconcile.running.useQuery(undefined, { refetchInterval: 4000 });
  const isReconRunning = running.data?.running ?? false;
  const afterRun = () => { utils.reconcile.changeSets.invalidate(); running.refetch(); };
  const runExact = trpc.reconcile.runExact.useMutation({ onSuccess: afterRun });
  const runLs = trpc.reconcile.runLs.useMutation({ onSuccess: afterRun });
  const deleteSet = trpc.reconcile.deleteSet.useMutation({ onSuccess: () => utils.reconcile.changeSets.invalidate() });
  const clearSets = trpc.reconcile.clearSets.useMutation({ onSuccess: () => utils.reconcile.changeSets.invalidate() });
  const recon = byJob["reconcile"];
  const liveRecon = recon && recon.percent >= 0 && recon.percent < 100;
  // The ExactOnline → Lightspeed reconcile is built from ExactOnline data — warn if ExactOnline changes
  // are still pending so the user finishes those first (then the LS plan is on fresh, final data).
  const exactReady = trpc.reconcile.exactReadiness.useQuery(undefined, { refetchInterval: 8000 });
  const startLs = () => {
    if (exactReady.data?.blocking && !confirm(`⚠️ ${exactReady.data.message}\n\nRun the ExactOnline → Lightspeed reconcile now anyway?`)) return;
    runLs.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Reconcile</h1>
        <div className="flex gap-2">
          <Button onClick={() => runExact.mutate()} disabled={runExact.isPending || isReconRunning}>Reconcile PBS → ExactOnline</Button>
          <Button variant="secondary" onClick={startLs} disabled={runLs.isPending || isReconRunning}>Reconcile ExactOnline → Lightspeed</Button>
          <Button variant="ghost" onClick={() => { if (confirm("Delete all change sets that have not been applied? This cannot be undone.")) clearSets.mutate({ all: false }); }} disabled={clearSets.isPending}>
            {clearSets.isPending ? "Clearing…" : "Clear unapplied"}
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Reconcile builds a <span className="font-medium text-slate-700">preview plan only</span> — it writes nothing to ExactOnline.
        Open a change set below to review each planned operation (field names, endpoint, and what changes), then post items
        one at a time with the <span className="font-medium text-slate-700">“Post 1 LIVE”</span> button when you’re confident.
      </p>

      {exactReady.data?.blocking && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">⚠ Finish the ExactOnline side first.</span> {exactReady.data.message}
        </div>
      )}

      {(liveRecon || isReconRunning) && (
        <Card>
          {liveRecon ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-sm font-semibold text-slate-800">{RECON_PHASES[recon!.step] ?? recon!.step}</span>
                <span className="text-xs tabular-nums text-slate-500">{recon!.percent}%</span>
              </div>
              {recon!.detail && <div className="text-xs text-slate-600">{recon!.detail}</div>}
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${Math.max(3, recon!.percent)}%` }} />
              </div>
              {/* The phases in order, so a long step reads as "this is step 3 of 4", not as a hang. */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                {RECON_ORDER.map((p) => {
                  const done = RECON_ORDER.indexOf(recon!.step) > RECON_ORDER.indexOf(p);
                  const now = recon!.step === p;
                  return (
                    <span key={p} className={now ? "font-semibold text-indigo-700" : done ? "text-slate-400 line-through" : "text-slate-400"}>
                      {now ? "▸ " : done ? "✓ " : "· "}{RECON_PHASES[p]}
                    </span>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400">Keeps running if you leave this page — the full log is on Sync &amp; Data.</p>
            </div>
          ) : (
            <Spinner label={
              running.data?.jobs?.[0]?.step
                ? `Reconcile running — ${running.data.jobs[0]!.step}${running.data.jobs[0]!.percent ? ` (${running.data.jobs[0]!.percent}%)` : ""}`
                : "Reconcile running… (it keeps running if you leave this page — progress is on Sync & Data)"
            } />
          )}
        </Card>
      )}

      <Card title="Change sets">
        {filtersActive && (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="font-medium text-indigo-700">
              Filtered — {(sets.data?.length ?? 0).toLocaleString()} set{(sets.data?.length ?? 0) === 1 ? "" : "s"} shown
            </span>
            {fFlow.map((v) => <Badge key={v} color="blue">{FLOW_LABELS[v] ?? v}</Badge>)}
            {fApproval.map((v) => <Badge key={v} color="amber">{v}</Badge>)}
            {fStatus.map((v) => <Badge key={v} color="gray">{v}</Badge>)}
            <button onClick={() => { setFFlow([]); setFStatus([]); setFApproval([]); }} className="font-medium text-slate-500 underline hover:text-slate-800">clear all filters</button>
          </div>
        )}
        {sets.isLoading ? (
          <Spinner />
        ) : !sets.data?.length ? (
          <p className="text-sm text-slate-500">
            {filtersActive ? (
              <>No change sets match these filters. <button onClick={() => { setFFlow([]); setFStatus([]); setFApproval([]); }} className="font-medium underline">Clear the filters</button> to see them all.</>
            ) : (
              "No change sets yet. Run a reconciliation above to generate a reviewable set of operations."
            )}
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2">ID</th>
                <th>
                  <ColumnFilter
                    title="Flow (source → target)"
                    selected={fFlow}
                    onChange={setFFlow}
                    options={(filterOptions.data?.targets ?? []).map((t) => ({ value: t.value as FlowValue, label: FLOW_LABELS[t.value] ?? t.value, count: t.count }))}
                  />
                </th>
                <th>Ops</th>
                <th>
                  <ColumnFilter
                    title="Approval"
                    selected={fApproval}
                    onChange={setFApproval}
                    options={(filterOptions.data?.approval ?? []).map((a) => ({ value: a.value as ApprovalValue, label: a.value, count: a.count }))}
                  />
                </th>
                <th>
                  <ColumnFilter
                    title="Status"
                    selected={fStatus}
                    onChange={setFStatus}
                    options={(filterOptions.data?.statuses ?? []).map((s) => ({ value: s.value as StatusValue, label: s.value, count: s.count }))}
                  />
                </th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sets.data.map((cs) => {
                const s = cs.summary as { total?: number; create?: number; update?: number; delete?: number; maxPriceDeltaPct?: number; excluded?: number; priceOnlySkipped?: number; noPriceSkipped?: number; noPriceDiscontinued?: number; byReason?: Record<string, number>; orphanSkipReason?: string | null; needsMapping?: number; excludedKeyword?: number; lsSnapshotAt?: string | null; lsSnapshotCount?: number; unitMismatchCount?: number } | null;
                return (
                  <tr key={cs.id} className="[&>td]:py-3 [&>td]:align-top">
                    <td className="py-2"><Link href={`/reconcile/${cs.id}`} className="font-medium text-blue-600">#{cs.id}</Link></td>
                    <td><Link href={`/reconcile/${cs.id}`} className="hover:underline" title="Open this change set"><FlowLabel target={cs.target} /></Link></td>
                    <td className="py-2 align-top text-slate-600">
                      <div className="font-semibold text-slate-800">{(s?.total ?? 0).toLocaleString()} ops</div>
                      {s?.byReason ? (
                        <OpsBreakdown byReason={s.byReason} />
                      ) : (
                        <div className="text-xs text-slate-400">{s?.create ?? 0} create / {s?.update ?? 0} update{(s?.delete ?? 0) > 0 ? ` / ${s!.delete} delete` : ""}</div>
                      )}
                      {(s?.excluded ?? 0) > 0 && <div className="mt-1 text-[11px] italic text-slate-400">{s!.excluded!.toLocaleString()} skipped (excluded category)</div>}
                      {(s?.priceOnlySkipped ?? 0) > 0 && <div className="mt-1 text-[11px] italic text-slate-400">{s!.priceOnlySkipped!.toLocaleString()} skipped (price-only — in PBS price list but not the product catalog)</div>}
                      {(s?.noPriceDiscontinued ?? 0) > 0 && (
                        <div className="mt-1 max-w-md text-[11px] text-amber-600">
                          ⚠ {s!.noPriceDiscontinued!.toLocaleString()} deactivated — PBS has no price for them, so they would be sellable at € 0,00. Lightspeed hides them on its own side.
                        </div>
                      )}
                      {(s?.noPriceSkipped ?? 0) > 0 && (
                        <div className="mt-1 max-w-md text-[11px] text-amber-600">
                          ⚠ {s!.noPriceSkipped!.toLocaleString()} skipped — in the PBS catalog but missing from PBS's price list, so there is no price to sync. Creating them would put € 0,00 in ExactOnline.
                        </div>
                      )}
                      {(s?.excludedKeyword ?? 0) > 0 && <div className="mt-1 text-[11px] italic text-slate-400">{s!.excludedKeyword!.toLocaleString()} skipped (excluded name keyword, e.g. statiegeld)</div>}
                      {(s?.needsMapping ?? 0) > 0 && (
                        <div className="mt-1 max-w-md text-[11px] text-amber-600">
                          ⚠ {s!.needsMapping!.toLocaleString()} in-stock item{s!.needsMapping === 1 ? "" : "s"} can be created once their category is mapped —{" "}
                          <Link href="/settings" className="font-medium underline">Settings → Mapping</Link>
                        </div>
                      )}
                      {cs.target === "lightspeed" && (s?.create ?? 0) > 0 && (
                        <div className="mt-1 max-w-md text-[11px] text-slate-500">
                          ℹ Creates are based on the Lightspeed snapshot{s?.lsSnapshotAt ? ` from ${fmtDateTime(s.lsSnapshotAt)}` : ""}{s?.lsSnapshotCount != null ? ` (${s.lsSnapshotCount.toLocaleString()} products)` : ""}. If a product that already exists on the shop shows as “create”, refresh the{" "}
                          <Link href="/sync" className="font-medium underline">Lightspeed snapshot</Link> and reconcile again. (Posting one anyway updates the existing product — it never makes a duplicate.)
                        </div>
                      )}
                      {s?.orphanSkipReason && <div className="mt-1 max-w-md text-[11px] text-amber-600">⚠ {s.orphanSkipReason}</div>}
                      {(s?.unitMismatchCount ?? 0) > 0 && (
                        <div className="mt-1 max-w-md text-[11px] text-amber-600">
                          ⚠ {s!.unitMismatchCount!.toLocaleString()} item{s!.unitMismatchCount === 1 ? "" : "s"} with a pack↔piece unit mismatch (ExactOnline unit can't be edited after creation) — price sync skipped; needs a manual accounting fix in ExactOnline.
                        </div>
                      )}
                    </td>
                    <td>{cs.requiresApproval ? <Badge color="amber">required</Badge> : <Badge color="green">auto</Badge>}</td>
                    <td className="align-top">
                      <Badge color={cs.status === "applied" ? "green" : cs.status.includes("fail") ? "red" : cs.status === "pending_approval" ? "amber" : "gray"}>{cs.status}</Badge>
                      {/* Mode badge reflects what's actually happening: real posts → LIVE; otherwise the creation-time dry-run flag. */}
                      {progress.data?.[cs.id]?.applied ? <span className="ml-1"><Badge color="red">LIVE</Badge></span> : cs.dryRun ? <span className="ml-1"><Badge color="blue">dry-run</Badge></span> : null}
                      {(() => {
                        const p = progress.data?.[cs.id];
                        if (!p || !(p.running || p.paused || p.interrupted || (p.processed > 0 && p.pending > 0))) return null;
                        const amber = p.interrupted || p.paused;
                        // While running, show how many operations go out at once — the single number that
                        // explains whether this will take ten minutes or an hour.
                        const retrying = p.running && p.pending === 0 && p.failed > 0;
                        const label = p.running
                          ? `${retrying ? "retrying" : "applying"}${p.lanes && p.lanes > 1 ? ` ×${p.lanes}` : ""}`
                          : p.paused ? "paused" : p.interrupted ? "interrupted" : "partial";
                        const pct = retrying && p.total > 0 ? Math.round((p.applied / p.total) * 100) : p.percent;
                        return (
                          <div className="mt-1.5 w-40">
                            <div className="mb-0.5 flex items-center justify-between text-[10px] tabular-nums">
                              <span className={amber ? "font-medium text-amber-700" : p.running ? "font-medium text-indigo-700" : "text-slate-500"}>{label}</span>
                              <span className="text-slate-500">{pct}%</span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                              <div className={`h-full transition-all ${amber ? "bg-amber-500" : "bg-indigo-600"}`} style={{ width: `${Math.max(3, pct)}%` }} />
                            </div>
                            <div className="mt-0.5 text-[10px] tabular-nums text-slate-400">{p.processed.toLocaleString("nl-NL")}/{p.total.toLocaleString("nl-NL")}{p.failed > 0 ? ` · ${p.failed} failed` : ""}</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-slate-500">{fmtDateTime(cs.createdAt)}</td>
                    <td className="align-top">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/reconcile/${cs.id}`} className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50" title="Open this change set">View</Link>
                        <Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete change set #${cs.id}? This removes its planned operations and cannot be undone.`)) deleteSet.mutate({ id: cs.id }); }}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
